import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Square } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useJobsList, useStopScrape } from '../hooks/scrape';
import type { Folder, JobSummary } from '../api/types';
import { useDiscoveryPaths } from '../workspace/paths';
import { MENU_CONTENT_CLS } from '../workspace/ui';
import { SoundText } from '../components/SoundLink';

/**
 * Running searches, as a small indicator in the Discovery header. Replaces the
 * recent-searches strip that sat above every screen: finished searches now
 * live in each folder's Activity tab, and only what's still running is global.
 */
export function RunningJobs({ folders, compact = false }: { folders: readonly Folder[]; compact?: boolean }): JSX.Element | null {
  const jobsQuery = useJobsList();
  const running = (jobsQuery.data ?? []).filter((j) => j.status === 'running');
  if (running.length === 0) return null;

  const folderName = (id: string) => folders.find((f) => f.publicId === id)?.name ?? 'Unknown folder';
  const label = `${running.length} search${running.length === 1 ? '' : 'es'} running`;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          title={label}
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-full border border-brand/30 bg-brand/10 text-xs font-semibold text-brand transition-colors hover:bg-brand/15',
            compact ? 'w-7 justify-center' : 'px-2.5',
          )}
        >
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand motion-reduce:animate-none" aria-hidden />
          {!compact && <span>{running.length} running</span>}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" sideOffset={6} className={cn(MENU_CONTENT_CLS, 'w-80 p-2')}>
          <p className="px-1 pb-1.5 text-sm font-semibold">{label}</p>
          <div className="space-y-1.5">
            {running.map((job) => (
              <RunningJobRow key={job.id} job={job} folderName={folderName(job.folderId)} />
            ))}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function RunningJobRow({ job, folderName }: { job: JobSummary; folderName: string }): JSX.Element {
  const navigate = useNavigate();
  const paths = useDiscoveryPaths();
  const stop = useStopScrape();
  const progress = job.targetCount > 0 ? Math.min(1, job.saved / job.targetCount) : 0;
  const stopping = stop.variables === job.id && (stop.isPending || stop.isSuccess);

  return (
    <DropdownMenu.Item
      onSelect={() => navigate(paths.folder(job.folderId, 'activity'))}
      className="block cursor-pointer rounded-lg border border-border px-2.5 py-2 outline-none focus:bg-accent"
    >
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium" title={job.keywords}>
          <SoundText text={job.keywords || 'Search'} />
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {job.saved}/{job.targetCount}
        </span>
      </div>
      <div className="mt-0.5 flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{folderName}</span>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          disabled={stopping}
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            stop.mutate(job.id);
          }}
          onPointerDown={(ev) => ev.stopPropagation()}
        >
          <Square className="h-2.5 w-2.5 fill-current" />
          {stopping ? 'Stopping…' : 'Stop'}
        </Button>
      </div>
      <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-brand transition-[width] duration-500" style={{ width: `${progress * 100}%` }} />
      </div>
    </DropdownMenu.Item>
  );
}

export function elapsedStr(startedAt: string, finishedAt: string | null): string {
  const start = new Date(startedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const sec = Math.max(0, Math.floor((end - start) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${sec % 60}s`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
}
